import { useMemo, useState } from 'react'
import {
  Badge, Button, HStack, NumberInput, Stack, Text, VStack, type NumberInputValueChangeDetails,
} from '@chakra-ui/react'
import { Card, ResponsiveTable, type ResponsiveColumn } from '../../../shared'
import { useIsMobile } from '../../../../hooks/useIsMobile'
import { useAvailableCanvassers } from '../../../../hooks/useCampaign'
import { STATIC_SENTINEL_KEY } from '../../itinerary/StaticCellDropZone'
import { shortDayLabel, toLocalDateStr } from './routeDates'
import { AssignRouteSheet } from './AssignRouteSheet'
import type { FollowUpRouteBatchDto, FollowUpRouteDto } from '../../../../types/followUp'

interface FollowUpRouteBatchListProps {
  campaignId: string
  batches: FollowUpRouteBatchDto[]
  selectedBatchId: string | null
  onSelectBatch: (turfMapId: string) => void
  onRecut: (turfMapId: string, driverCount: number) => void
  isBusy: boolean
  clampDrivers: (n: number) => number
  maxDrivers: number | undefined
}

export function FollowUpRouteBatchList({
  campaignId, batches, selectedBatchId, onSelectBatch, onRecut, isBusy, clampDrivers, maxDrivers,
}: FollowUpRouteBatchListProps) {
  const isMobile = useIsMobile()
  const [assignRoute, setAssignRoute] = useState<FollowUpRouteDto | null>(null)
  // Names for the Assigned column: the campaign's assignable canvassers (today's roster). A grant
  // that isn't on it (unavailable today, revoked) shows a plain "Assigned".
  const today = useMemo(() => toLocalDateStr(new Date()), [])
  const { data: available } = useAvailableCanvassers(campaignId, batches.length > 0 ? today : undefined)
  const nameByGrant = useMemo(
    () => new Map((available?.canvassers ?? []).map(c => [c.grantId, c.fullName])),
    [available],
  )

  const assignedText = (r: FollowUpRouteDto) => {
    if (r.assignments.length === 0) return '—'
    return r.assignments.map(a => {
      const name = (a.grantId && nameByGrant.get(a.grantId)) || 'Assigned'
      return a.scheduledDate === STATIC_SENTINEL_KEY ? name : `${name} · ${shortDayLabel(a.scheduledDate)}`
    }).join(', ')
  }

  const columns: ResponsiveColumn<FollowUpRouteDto>[] = [
    { header: 'Route', mobileRole: 'title', cell: r => r.label },
    { header: 'Stops', mobileRole: 'meta', isNumeric: true, cell: r => r.stopCount },
    {
      header: 'Done/Open', mobileRole: 'badge',
      cell: r => <Badge variant="outline" fontSize="2xs">{r.closedItemCount} done / {r.openItemCount} open</Badge>,
    },
    { header: 'Assigned', mobileRole: 'meta', cell: r => <Text as="span" lineClamp={2}>{assignedText(r)}</Text> },
    {
      header: 'Actions', mobileRole: 'desktop-only',
      cell: r => <Button size="sm" variant="outline" onClick={() => setAssignRoute(r)}>Assign</Button>,
    },
  ]

  if (batches.length === 0) return null

  return (
    <VStack align="stretch" gap={3}>
      {batches.map(batch => (
        <BatchCard
          key={batch.turfMapId}
          batch={batch}
          selected={batch.turfMapId === selectedBatchId}
          onSelect={() => onSelectBatch(batch.turfMapId)}
          onRecut={(n) => onRecut(batch.turfMapId, n)}
          isBusy={isBusy}
          clampDrivers={clampDrivers}
          maxDrivers={maxDrivers}
        >
          <ResponsiveTable
            bare
            columns={columns}
            rows={batch.routes}
            keyFn={r => r.turfId}
            onRowClick={isMobile ? (r) => setAssignRoute(r) : undefined}
            emptyMessage="No routes in this batch."
          />
        </BatchCard>
      ))}
      <AssignRouteSheet
        isOpen={!!assignRoute}
        onClose={() => setAssignRoute(null)}
        campaignId={campaignId}
        route={assignRoute}
      />
    </VStack>
  )
}

function BatchCard({
  batch, selected, onSelect, onRecut, isBusy, clampDrivers, maxDrivers, children,
}: {
  batch: FollowUpRouteBatchDto
  selected: boolean
  onSelect: () => void
  onRecut: (driverCount: number) => void
  isBusy: boolean
  clampDrivers: (n: number) => number
  maxDrivers: number | undefined
  children: React.ReactNode
}) {
  const [driversText, setDriversText] = useState('1')
  const drivers = clampDrivers(Number(driversText))
  const hasUnlocked = batch.routes.some(r => !r.isLocked)

  return (
    <Card p={{ base: 3, md: 4 }} borderColor={selected ? 'accent' : 'border'}>
      <VStack align="stretch" gap={3}>
        <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={2}>
          <HStack gap={2} minW={0}>
            <Text fontWeight="semibold" lineClamp={1}>{batch.label}</Text>
            <Text fontSize="xs" color="muted" flexShrink={0}>{batch.routes.length} routes</Text>
          </HStack>
          {!selected && (
            <Button size="sm" variant="ghost" minH="44px" onClick={onSelect}>Show on map</Button>
          )}
        </Stack>
        {children}
        {hasUnlocked && (
          <Stack direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }} gap={2}>
            <HStack gap={2} flexWrap="wrap">
              <Text fontSize="sm">Re-cut unassigned for</Text>
              <NumberInput.Root
                size="sm"
                w="88px"
                min={1}
                max={maxDrivers}
                value={driversText}
                onValueChange={(d: NumberInputValueChangeDetails) => setDriversText(d.value)}
              >
                <NumberInput.Input aria-label="Re-cut drivers" minH="44px" />
                <NumberInput.Control>
                  <NumberInput.IncrementTrigger />
                  <NumberInput.DecrementTrigger />
                </NumberInput.Control>
              </NumberInput.Root>
              <Text fontSize="sm">drivers</Text>
            </HStack>
            <Button size="sm" variant="outline" minH="44px" loading={isBusy} disabled={isBusy} onClick={() => onRecut(drivers)}>
              Re-cut
            </Button>
          </Stack>
        )}
      </VStack>
    </Card>
  )
}
